/**
 * Streaming API utilities for real-time M.ai.ry responses
 * Provides hooks and functions for consuming Server-Sent Events
 */
import { useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Stream M.ai.ry chat responses in real-time
 * @param {Object} params - Request parameters
 * @param {string} params.player_id - User/player ID
 * @param {string} params.message - User message
 * @param {string} params.session_id - Chat session ID
 * @param {Function} onChunk - Callback for each chunk of streamed content
 * @param {Function} onComplete - Callback when streaming completes
 * @param {Function} onError - Callback for errors
 */
export async function streamMairyChat({
  player_id,
  message,
  session_id,
  story_context = {},
}, onChunk, onComplete, onError) {
  try {
    const response = await fetch(`${BACKEND_URL}/api/mairy/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        player_id,
        message,
        session_id,
        story_context,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      // Decode the chunk
      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE messages
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || ''; // Keep incomplete message in buffer
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            
            if (data.error) {
              onError && onError(new Error(data.error));
              return;
            }
            
            if (data.done) {
              onComplete && onComplete(data.full_response || '');
            } else if (data.content) {
              onChunk && onChunk(data.content);
            }
          } catch (parseError) {
            console.error('Error parsing SSE data:', parseError);
          }
        }
      }
    }
  } catch (error) {
    console.error('Streaming error:', error);
    onError && onError(error);
  }
}

/**
 * React hook for streaming M.ai.ry chat
 * @returns {Object} Hook utilities
 */
export function useStreamingMairyChat() {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedContent, setStreamedContent] = useState('');
  const [error, setError] = useState(null);

  const sendMessage = useCallback(async (params) => {
    setIsStreaming(true);
    setStreamedContent('');
    setError(null);

    return new Promise((resolve, reject) => {
      streamMairyChat(
        params,
        // onChunk
        (chunk) => {
          setStreamedContent((prev) => prev + chunk);
        },
        // onComplete
        (fullResponse) => {
          setIsStreaming(false);
          resolve(fullResponse);
        },
        // onError
        (err) => {
          setIsStreaming(false);
          setError(err);
          reject(err);
        }
      );
    });
  }, []);

  const reset = useCallback(() => {
    setStreamedContent('');
    setError(null);
    setIsStreaming(false);
  }, []);

  return {
    isStreaming,
    streamedContent,
    error,
    sendMessage,
    reset,
  };
}

// For non-React usage
export default {
  streamMairyChat,
};
